import { useEffect } from 'react';
import { Copy, Check, Sparkles, Plus, ArrowRight, Trash2, Bookmark, ShieldCheck, X } from 'lucide-react';
import { useStore } from '../store';
import { PageLoader } from '../components/ui';
import type { RctoFields, TemplateView, UserTemplateView } from '../types';

const eyebrow: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.16em',
  textTransform: 'uppercase',
  color: '#00A800',
  marginBottom: 12,
};
const h2: React.CSSProperties = { fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em', margin: 0 };
const card: React.CSSProperties = { background: '#fff', border: '1px solid #E2E2E6', borderRadius: 12, padding: 20 };
const btn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600,
  padding: '8px 13px', borderRadius: 6, cursor: 'pointer', border: '1px solid #E2E2E6', background: '#fff', color: '#000',
};
const btnPrimary: React.CSSProperties = { ...btn, background: '#00D700', border: '1px solid #00D700' };
const FIELD_LABEL: Record<keyof RctoFields, string> = { role: 'Role', context: 'Context', task: 'Task', outputFormat: 'Output format' };

// Per-tool "where to keep it" guidance. Falls back to generic copy for unlisted tools.
const TOOL_GUIDANCE: Record<string, string> = {
  claude: 'Save it as a Project in Claude so it’s one click next time.',
  'microsoft copilot': 'Pin it as a saved prompt in Copilot.',
  copilot: 'Pin it as a saved prompt in Copilot.',
  chatgpt: 'Save it as a Custom GPT or a saved prompt in ChatGPT.',
  gemini: 'Save it as a Gem in Gemini.',
};
const toolTip = (tool: string) => TOOL_GUIDANCE[tool.toLowerCase()] ?? `Keep it as a saved/reusable prompt in ${tool}.`;

export function Templates() {
  const loadTemplates = useStore((s) => s.loadTemplates);
  const library = useStore((s) => s.libraryTemplates);
  const userTemplates = useStore((s) => s.userTemplates);
  const approvedTools = useStore((s) => s.approvedTools);
  const roleLabel = useStore((s) => s.templatesRoleLabel);
  const loading = useStore((s) => s.templatesLoading);
  const error = useStore((s) => s.templatesError);
  const showAllRoles = useStore((s) => s.showAllRoles);
  const toggleShowAllRoles = useStore((s) => s.toggleShowAllRoles);
  const regenerate = useStore((s) => s.regenerateTemplates);
  const openBuilder = useStore((s) => s.openBuilder);
  const builderOpen = useStore((s) => s.builderOpen);

  useEffect(() => loadTemplates(), [loadTemplates]);

  const roleSet = library.filter((t) => t.roleSlug !== 'general');
  const generalSet = library.filter((t) => t.roleSlug === 'general');
  const shown = showAllRoles || roleSet.length === 0 ? [...roleSet, ...generalSet] : roleSet;
  // Full-page veil only on the first load (nothing to show yet); a regenerate over existing
  // templates keeps its lighter inline indicator rather than blocking the whole screen.
  const firstLoad = loading && library.length === 0 && userTemplates.length === 0;

  return (
    <div style={{ flex: 1, background: '#F4F4F6' }}>
      <div style={{ maxWidth: 960, margin: '0 auto', padding: '36px 32px 80px' }}>
        {/* header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap', marginBottom: 22 }}>
          <div>
            <div style={eyebrow}>Templates</div>
            <h1 style={h2}>Prompt templates</h1>
            <div style={{ fontSize: 14, color: '#75757F', marginTop: 7 }}>
              Reusable Role · Context · Task · Output prompts{roleLabel ? ` for ${roleLabel}` : ''}. AI-generated starting points — review and adapt before use.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button style={btn} onClick={() => regenerate()} disabled={loading}><Sparkles size={15} /> Regenerate</button>
            <button style={btnPrimary} onClick={() => openBuilder()}><Plus size={15} /> Build your own</button>
          </div>
        </div>

        {/* approved-tool guidance */}
        {approvedTools.length > 0 && (
          <div style={{ ...card, padding: '14px 18px', marginBottom: 20, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <ShieldCheck size={18} color="#00A800" style={{ flex: 'none', marginTop: 1 }} />
            <div style={{ fontSize: 13.5, color: '#333544' }}>
              <strong>Keep templates in your approved tool.</strong>{' '}
              {approvedTools.map((t) => toolTip(t)).join(' ')}
            </div>
          </div>
        )}

        {error && <div style={{ ...card, borderColor: '#F2C9CE', color: '#E5142B', marginBottom: 20 }}>{error}</div>}
        <PageLoader show={firstLoad} label="Generating templates for your role…" />
        {loading && !firstLoad && <div style={{ ...card, color: '#75757F', marginBottom: 20 }}>Generating templates for your role…</div>}

        {builderOpen && <Builder />}

        {/* My templates */}
        {userTemplates.length > 0 && (
          <Section title="My templates">
            <Grid>{userTemplates.map((t) => <UserCard key={t.templateId} t={t} />)}</Grid>
          </Section>
        )}

        {/* Library */}
        <Section
          title="Library"
          right={
            roleSet.length > 0 && generalSet.length > 0 ? (
              <button style={{ ...btn, padding: '5px 11px', fontSize: 12 }} onClick={toggleShowAllRoles}>
                {showAllRoles ? 'Role-specific only' : 'Show general too'}
              </button>
            ) : undefined
          }
        >
          {shown.length === 0 && !loading ? (
            <div style={{ ...card, color: '#75757F' }}>No templates yet. Try “Regenerate”.</div>
          ) : (
            <Grid>{shown.map((t) => <LibraryCard key={t.templateId} t={t} />)}</Grid>
          )}
        </Section>
      </div>
    </div>
  );
}

function Section({ title, right, children }: { title: string; right?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>{title}</h2>
        {right}
      </div>
      {children}
    </div>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }}>{children}</div>;
}

function Rcto({ fields }: { fields: RctoFields }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7, margin: '12px 0' }}>
      {(Object.keys(FIELD_LABEL) as (keyof RctoFields)[]).map((k) => (
        <div key={k} style={{ fontSize: 12.5, lineHeight: 1.45 }}>
          <span style={{ fontWeight: 700, color: '#00A800' }}>{FIELD_LABEL[k]}:</span>{' '}
          <span style={{ color: '#333544' }}>{fields[k]}</span>
        </div>
      ))}
    </div>
  );
}

function CopyBtn({ text }: { text: string }) {
  const copyTemplate = useStore((s) => s.copyTemplate);
  const copied = useStore((s) => s.copied);
  return (
    <button style={btn} onClick={() => copyTemplate(text)}>
      {copied ? <Check size={15} color="#00A800" /> : <Copy size={15} />} {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

function LibraryCard({ t }: { t: TemplateView }) {
  const useTemplate = useStore((s) => s.useTemplate);
  const saveLibraryTemplate = useStore((s) => s.saveLibraryTemplate);
  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 15, fontWeight: 700 }}>{t.title}</span>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#75757F', background: '#F0F0F2', borderRadius: 999, padding: '2px 8px' }}>
          {t.source === 'fallback' ? 'Starter' : 'AI-generated'}
        </span>
      </div>
      {t.summary && <div style={{ fontSize: 13, color: '#75757F' }}>{t.summary}</div>}
      <Rcto fields={t.fields} />
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <CopyBtn text={t.prompt} />
        <button style={btn} onClick={() => useTemplate(t.prompt)}>Use in Practice <ArrowRight size={15} /></button>
        <button style={btn} onClick={() => saveLibraryTemplate(t)}><Bookmark size={15} /> Save a copy</button>
      </div>
    </div>
  );
}

function UserCard({ t }: { t: UserTemplateView }) {
  const useTemplate = useStore((s) => s.useTemplate);
  const deleteTemplate = useStore((s) => s.deleteTemplate);
  return (
    <div style={card}>
      <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>{t.title}</div>
      <Rcto fields={t.fields} />
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <CopyBtn text={t.prompt} />
        <button style={btn} onClick={() => useTemplate(t.prompt)}>Use in Practice <ArrowRight size={15} /></button>
        <button style={{ ...btn, color: '#E5142B', borderColor: '#F2C9CE' }} onClick={() => deleteTemplate(t.templateId)}><Trash2 size={15} /> Delete</button>
      </div>
    </div>
  );
}

function Builder() {
  const title = useStore((s) => s.builderTitle);
  const fields = useStore((s) => s.builderFields);
  const setTitle = useStore((s) => s.setBuilderTitle);
  const setField = useStore((s) => s.setBuilderField);
  const save = useStore((s) => s.saveBuilder);
  const close = useStore((s) => s.closeBuilder);
  const ta: React.CSSProperties = { width: '100%', fontSize: 13, padding: '9px 11px', borderRadius: 6, border: '1px solid #E2E2E6', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' };
  return (
    <div style={{ ...card, marginBottom: 28 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Build a template</h2>
        <button style={{ ...btn, border: 'none', padding: 6 }} onClick={close} aria-label="Close"><X size={17} /></button>
      </div>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#75757F', marginBottom: 5 }}>Title</label>
      <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Weekly status update" style={{ ...ta, marginBottom: 14 }} />
      {(Object.keys(FIELD_LABEL) as (keyof RctoFields)[]).map((k) => (
        <div key={k} style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#00A800', marginBottom: 5 }}>{FIELD_LABEL[k]}</label>
          <textarea value={fields[k]} onChange={(e) => setField(k, e.target.value)} rows={2} style={ta} />
        </div>
      ))}
      <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
        <button style={btnPrimary} onClick={save}>Save template</button>
        <button style={btn} onClick={close}>Cancel</button>
      </div>
    </div>
  );
}
